import React, { useMemo } from 'react';
import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import { useTranslation } from 'react-i18next';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { InputLockedPeriod } from 'structure/staking/components/form/InputLockedPeriod';
import { StakeDetails } from 'structure/staking/components/StakeDetails';
import { useDeposit } from 'structure/staking/hooks/useStakingContract';
import { waitForTransaction } from 'utils/waitForTransaction';
import {
  useStakingExtendCalcInfo,
  useStakingPoolData,
  useStakingPoolList,
  useStakingUserPoolInfo,
  useUpdatePoolMetadata,
  useWalletSREP,
} from 'structure/staking/hooks/useStakingAPI';
import { daysToSeconds, secondsToDays } from 'utils/date';
import { createExtendValidation } from 'structure/staking/utils/createValidationSchema';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useChainBlock } from 'utils/hooks/useChainBlock';
import { useConnection } from 'wagmi';
import { QueryKey } from 'utils/constants/query-keys.enum';
import {
  calcNewLockedPeriodInSeconds,
  calcCurrentLockedPeriodInSeconds,
} from 'structure/staking/utils/userPollInfo.utils';
import { useParams } from 'next/navigation';
import { Title4 } from 'components/Typography';
import { StakingPool } from 'api/StakingAPI';

type FormData = {
  lockedPeriod?: number;
};

type DepositModalProps = {
  open: boolean;
  poolId: number;
  onOpenChange: (isOpen: boolean) => void;
};

export function ModalStakeExtend({
  open,
  poolId,
  onOpenChange,
}: DepositModalProps) {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile(834);
  const toastV2 = useToast();
  const account = useConnection();
  const params = useParams<{ id: string }>();
  const { data: chainBlock } = useChainBlock({
    queryKey: [QueryKey.CHAIN_BLOCK],
    watch: false,
  });

  const { data = [] } = useStakingPoolList({}, params?.id ?? '');
  const initialPool = data.find((pool) => pool.id === poolId);
  const stakingData = useStakingPoolData(initialPool as StakingPool);
  const pool = stakingData?.data;

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const userPoolInfo = useStakingUserPoolInfo({
    poolContractAddress: pool.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool.chainId,
    pool,
  });

  const currentLockedPeriodInSeconds = calcCurrentLockedPeriodInSeconds(
    userPoolInfo.data,
    chainBlock.timestamp
  );

  const maxExtendDays = Math.floor(
    secondsToDays(
      Number(pool.MAX_LOCKING_PERIOD) - currentLockedPeriodInSeconds
    )
  );

  const schema = createExtendValidation({
    lockedPeriod: {
      label: t('STAKING.TOKEN_AMOUNT'),
      min: Math.min(1, maxExtendDays),
      max: maxExtendDays,
      required: true,
    },
  });

  const form = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues: {
      lockedPeriod: 1,
    },
  });

  const maxLockingPeriod = useMemo(() => {
    return secondsToDays(Number(pool.MAX_LOCKING_PERIOD));
  }, [pool]);

  const lockedPeriod = form.watch('lockedPeriod');

  const handleChangeLockedPeriod = (value: number) => {
    form.setValue('lockedPeriod', value);
    form.trigger('lockedPeriod');
  };

  const newLockedPeriod = calcNewLockedPeriodInSeconds(
    userPoolInfo.data,
    daysToSeconds(lockedPeriod),
    chainBlock.timestamp
  );

  const deposit = useDeposit({
    contractAddress: pool.stakingContractAddress,
    chainId: pool.chainId,
    tokenAmount: '0',
    lockedPeriod: newLockedPeriod,
    enabled: open && form.formState.isValid,
  });

  const stakingCalculation = useStakingExtendCalcInfo({
    poolContractAddress: pool.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool.chainId,
    lockedPeriod: daysToSeconds(lockedPeriod),
    query: {
      enabled: !!account.address && form.formState.isValid && open,
    },
  });

  const walletSREPData = useWalletSREP({
    walletAddress: account.address as `0x${string}`,
    query: {
      enabled: !!account.address,
    },
  });

  const updatePoolData = useUpdatePoolMetadata();

  const handleExtend = async () => {
    try {
      const simulation = await deposit.prepareFresh();
      const result = await deposit.contract.writeContractAsync(
        simulation.request
      );

      await waitForTransaction({
        chainId: pool.chainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      await updatePoolData.mutateAsync();

      toastV2({
        title: t('STAKING.EXTEND_SUCCESS'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await walletSREPData.refetch();
      handleOpenChange(false);
    } catch (error) {
      console.log('### deposit', deposit);
      console.log('### extent error', error);
      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: t('STAKING.FAILED_TO_REQUEST_EXTEND'),
        type: 'error',
      });
    }
  };

  const handleSubmit = async () => {
    await handleExtend();
  };

  const isLockedPeriodLimitReached = maxExtendDays === 0;

  return (
    <ModalV2.Root open={open} onOpenChange={handleOpenChange}>
      <ModalV2.Content variant='dark' fullscreen={isMobile}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className='flex flex-row items-center justify-between'>
            <ModalV2.Title variant='dark'>
              <Title4>{t('STAKING.MODAL_EXTEND_TITLE')}</Title4>
            </ModalV2.Title>
            <ModalV2.Close variant='dark' />
          </div>
          <ModalV2.BodyTitle
            variant='dark'
            // endEnhancer={`${t('STAKING.CURRENT_TIER')} : ${tier.name}`}
          >
            {t('STAKING.EXTEND_LOCKED_PERIOD_BY')}
          </ModalV2.BodyTitle>
          <ModalV2.Body variant='dark'>
            <InputLockedPeriod
              onChanePeriod={handleChangeLockedPeriod}
              lockingPeriods={pool.defaultLockingPeriods}
              error={
                isLockedPeriodLimitReached
                  ? t('STAKING.REACH_LIMIT_OF_LOCKED_PERIOD', {
                      value: maxLockingPeriod,
                    })
                  : form.formState.errors.lockedPeriod?.message
              }
              disabled={isLockedPeriodLimitReached}
              {...form.register('lockedPeriod')}
            />
            <StakeDetails
              userPoolInfo={userPoolInfo.data}
              action='stake'
              pool={pool}
              loading={stakingCalculation.isFetching}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              calculation={stakingCalculation.data as any}
            />
          </ModalV2.Body>
          <div className='mt-6 flex justify-end gap-4 px-8'>
            <Button
              onClick={() => handleOpenChange(false)}
              disabled={deposit.contract.isPending}
              style='outline'
            >
              {t('STAKING.CANCEL')}
            </Button>

            <Button
              type='submit'
              loading={
                deposit.contract.isPending ||
                deposit.prepare.isFetching ||
                form.formState.isSubmitting
              }
              disabled={
                deposit.prepare.isFetching ||
                deposit.contract.isPending ||
                form.formState.isSubmitting ||
                !form.formState.isValid
              }
            >
              {t('STAKING.EXTEND')}
            </Button>
          </div>
        </form>
      </ModalV2.Content>
    </ModalV2.Root>
  );
}
